import {
  FACTORY_DECISIONS_LANG,
  FACTORY_QUESTIONS_LANG,
  findDecisionsBlock,
  findFencedBlock,
  parseFactoryQuestionsJson,
  parseQuestionOptions,
  recommendedOption,
  type FactoryQuestionOption,
  type FactoryQuestions,
} from './factory-questions';
import { backlogItemRound, findFactoryBacklog, type BacklogOutcome } from './factory-backlog';
import { indentFollowingLines, readFollowingLines } from './list-item-lines';

/**
 * T-14: further changes the coding agent suggests at the end of a run. Its final report ends with one
 * fenced block,
 *
 * ```factory-suggestions
 * { "suggestions": [{ "id": "retry", "title": "Retry the sync on a timeout", "detail": "…" }] }
 * ```
 *
 * The task shows each suggestion with what can be done about it (SUGGESTION_ACTIONS). People pick an
 * action per suggestion and apply them together: the factory records the decisions as one chat
 * message (formatSuggestionDecisions), which the card reads back (parseSuggestionDecisions).
 *
 * SF-T-308: a factory-questions block in the wrap phase is a round of suggestions too (findFactorySuggestions
 * reads it as one), and an item can offer options: picking one is Yes please, with the option(s) and any steer
 * in the decision's note. Skip is what No thanks was (the action is still `no`; old messages say No thanks).
 */

export const FACTORY_SUGGESTIONS_LANG = 'factory-suggestions';

/** Most suggestions in one block, and characters per piece of text. */
export const FACTORY_SUGGESTIONS_MAX = 10;
const TITLE_MAX = 200;
const DETAIL_MAX = 2000;
/** Characters in what building a suggestion would change, or leaving it out would mean (SF-T-300). */
const OPTION_MAX = 500;

/** The action a suggestion recommends, with the spellings an agent might use; null for Ask or anything else. */
function recommendedAction(value: unknown): Exclude<SuggestionAction, 'ask'> | null {
  const v = typeof value === 'string' ? value.trim().toLowerCase().replace(/[_-]+/g, ' ') : '';
  if (v === 'yes' || v === 'yes please') return 'yes';
  if (v === 'branch' || v === 'new branch') return 'branch';
  if (v === 'backlog') return 'backlog';
  if (v === 'no' || v === 'no thanks' || v === 'skip') return 'no';
  return null;
}

/** Characters in a decision's note (a reason, a question, or SF-T-308's options picked and steer: room for eight 200-character options, and ten notes still fit one message). */
export const SUGGESTION_NOTE_MAX = 2500;

export interface FactorySuggestion {
  id: string;
  title: string;
  /** Why, and what it would involve. */
  detail?: string;
  /** SF-T-300: what building it would change, and how big it is. */
  change?: string;
  /** SF-T-300: what stays as it is without it, and the risk. */
  without?: string;
  /** SF-T-300: the action the agent recommends (never Ask). */
  recommend?: Exclude<SuggestionAction, 'ask'>;
  /** SF-T-308: ways to do it, to pick from (none: Yes please alone). */
  options?: FactoryQuestionOption[];
  /** SF-T-308: several options can be picked together. */
  multiSelect?: boolean;
  /** SF-T-308: the label of the option the agent recommends. */
  recommended?: string;
}

export interface FactorySuggestions {
  suggestions: FactorySuggestion[];
}

/**
 * What can be done with a suggestion:
 * - yes: build it on this task's branch and PR (the AI builder carries on the thread).
 * - branch: build it as a new task, stacked on this task's branch, with its own PR.
 * - backlog: a new task in Backlog, for later.
 * - no: Skip (SF-T-308; No thanks before): we decided not to; the note says why (optional).
 * - ask: a question for the agent about it (the note), without deciding yet.
 */
export const SUGGESTION_ACTIONS = ['yes', 'branch', 'backlog', 'no', 'ask'] as const;
export type SuggestionAction = (typeof SUGGESTION_ACTIONS)[number];

export const SUGGESTION_ACTION_LABELS: Record<SuggestionAction, string> = {
  yes: 'Yes please',
  branch: 'New branch',
  backlog: 'Backlog',
  no: 'Skip',
  ask: 'Ask',
};

/** SF-T-308: what No thanks is called now; decisions messages from before say No thanks, read as Skip. */
const OLD_NO_LABEL = 'No thanks';

export function isSuggestionAction(value: unknown): value is SuggestionAction {
  return typeof value === 'string' && (SUGGESTION_ACTIONS as readonly string[]).includes(value);
}

/** An Ask needs its question; the other actions take an optional note (shown for No thanks). */
export const suggestionNoteRequired = (action: SuggestionAction) => action === 'ask';

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactorySuggestionsJson(raw: string): FactorySuggestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of suggestions is accepted too.
  const obj = (Array.isArray(data) ? { suggestions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.suggestions)) return null;
  const suggestions: FactorySuggestion[] = [];
  const ids = new Set<string>();
  for (const [i, item] of obj.suggestions.slice(0, FACTORY_SUGGESTIONS_MAX).entries()) {
    const s = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    // Titles are one line: they're the key the decisions message shows.
    const title = text(s.title, TITLE_MAX).replace(/\s*\n\s*/g, ' ');
    if (!title) continue;
    let id = text(s.id, 60) || `s${i + 1}`;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    const detail = text(s.detail ?? s.description, DETAIL_MAX);
    const change = text(s.change, OPTION_MAX);
    const without = text(s.without, OPTION_MAX);
    const recommend = recommendedAction(s.recommend);
    // SF-T-308: options read as a question's are (labels, descriptions, the recommended one).
    const options = parseQuestionOptions(s.options);
    const recommended = recommendedOption(s.recommended, options);
    suggestions.push({
      id,
      title,
      ...(detail ? { detail } : {}),
      ...(change ? { change } : {}),
      ...(without ? { without } : {}),
      ...(recommend ? { recommend } : {}),
      ...(options.length ? { options, ...(s.multiSelect === true ? { multiSelect: true } : {}), ...(recommended ? { recommended } : {}) } : {}),
    });
  }
  return suggestions.length ? { suggestions } : null;
}

/** SF-T-308: a decisions block's items (the wrap phase) as suggestions: the question is the title. */
export function suggestionsFromDecisions(decisions: FactoryQuestions): FactorySuggestions {
  const ids = new Set<string>();
  const suggestions = decisions.questions.slice(0, FACTORY_SUGGESTIONS_MAX).map((q, i): FactorySuggestion => {
    let id = q.id;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    const recommend = recommendedAction(q.recommend);
    const title = (q.header && q.question.length > TITLE_MAX ? q.header : q.question).replace(/\s*\n\s*/g, ' ').slice(0, TITLE_MAX);
    return {
      id,
      title,
      ...(q.detail ? { detail: q.detail } : {}),
      ...(q.change ? { change: q.change } : {}),
      ...(q.without ? { without: q.without } : {}),
      ...(recommend ? { recommend } : {}),
      ...(q.options.length ? { options: q.options, ...(q.multiSelect ? { multiSelect: true } : {}), ...(q.recommended ? { recommended: q.recommended } : {}) } : {}),
    };
  });
  return { suggestions };
}

/**
 * The message's suggestions, with where they sit: its first factory-suggestions block, else (SF-T-308) its
 * decisions block in the wrap phase. Null when there's neither (or it's broken).
 */
export function findFactorySuggestions(message: string): { suggestions: FactorySuggestions; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_SUGGESTIONS_LANG);
  const suggestions = block ? parseFactorySuggestionsJson(block.body) : null;
  if (block && suggestions) return { suggestions, start: block.start, end: block.end };
  const decisions = findDecisionsBlock(message);
  if (decisions?.questions.phase !== 'wrap') return null;
  return { suggestions: suggestionsFromDecisions(decisions.questions), start: decisions.start, end: decisions.end };
}

/**
 * SF-T-308: a fenced block's text, by its language, as the card it shows: questions (a grill-phase decisions
 * block), suggestions (a factory-suggestions block, or a decisions block in the wrap phase), or null.
 */
export function parseDecisionsCode(lang: string, text: string): { questions: FactoryQuestions } | { suggestions: FactorySuggestions } | null {
  if (lang === FACTORY_SUGGESTIONS_LANG) {
    const suggestions = parseFactorySuggestionsJson(text);
    return suggestions ? { suggestions } : null;
  }
  if (lang !== FACTORY_QUESTIONS_LANG && lang !== FACTORY_DECISIONS_LANG) return null;
  const questions = parseFactoryQuestionsJson(text);
  if (!questions) return null;
  return questions.phase === 'wrap' ? { suggestions: suggestionsFromDecisions(questions) } : { questions };
}

/** SF-T-170: the suggestions as a factory-suggestions block again, e.g. the ones sent to a source that reads blocks. */
export function formatFactorySuggestionsBlock(suggestions: FactorySuggestions): string {
  return '```' + FACTORY_SUGGESTIONS_LANG + '\n' + JSON.stringify({ suggestions: suggestions.suggestions }, null, 2) + '\n```';
}

/** One suggestion's decision. `number` is its place in the block (1-based); `note` is a reason, question or task ref. */
export interface SuggestionDecision {
  number: number;
  title: string;
  action: SuggestionAction;
  note: string;
  /** SF-T-308: a Backlog the AI builder made: the task it reported making, or why it made none (factory-backlog). */
  outcome?: BacklogOutcome;
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
const cleanTitle = (s: string) => oneLine(s).replace(/\*\*/g, '');
/** How far a note's further lines are indented under its decision (SF-T-265). */
const NOTE_INDENT = 3;

/** How a decisions message names one factory message: the start of its id (SF-T-62). */
export const suggestionsMessageRef = (messageId: string) => messageId.replace(/-/g, '').slice(0, 8).toLowerCase();

/**
 * The decisions message's first line: which run's suggestions they are (null: the newest before it).
 * `messageRef` names which of that run's messages (suggestionsMessageRef), when it posted more than
 * one (SF-T-62): "Suggestions from R-12 · 3f9a1c2e". Without it, the run's newest before the decisions.
 */
export function suggestionDecisionsHeader(runNumber: number | null, messageRef: string | null = null): string {
  if (runNumber === null) return '**Suggestions**';
  return `**Suggestions from R-${runNumber}${messageRef ? ` · ${messageRef}` : ''}**`;
}

/**
 * The decisions as the chat message the factory posts for the person who applied them. One line each,
 * `2. **Title** → No thanks: the reason`; Backlog and New branch carry the new task's ref.
 * `footer` follows (the @AI builder line, when the agent has work).
 */
export function formatSuggestionDecisions(runNumber: number | null, decisions: SuggestionDecision[], footer?: string, messageRef: string | null = null): string {
  const lines = [suggestionDecisionsHeader(runNumber, messageRef), ''];
  for (const d of decisions) {
    // SF-T-265: a note of several lines keeps them, indented under its decision.
    const note = indentFollowingLines(d.note, NOTE_INDENT);
    lines.push(`${d.number}. **${cleanTitle(d.title)}** → ${SUGGESTION_ACTION_LABELS[d.action]}${note ? `: ${note}` : ''}`);
  }
  if (footer) lines.push('', footer);
  return lines.join('\n');
}

const HEADER_RE = /^\*\*Suggestions(?: from R-(\d+)(?: · ([0-9a-f]{8}))?)?\*\*$/;
const LABEL_ACTIONS = new Map<string, SuggestionAction>([...SUGGESTION_ACTIONS.map((a): [string, SuggestionAction] => [SUGGESTION_ACTION_LABELS[a], a]), [OLD_NO_LABEL, 'no']]);
const LINE_RE = new RegExp(`^(\\d+)\\.\\s+\\*\\*(.*?)\\*\\*\\s+→\\s+(${[...LABEL_ACTIONS.keys()].join('|')})(?::\\s*(.*))?$`);

/**
 * Reads back a message formatSuggestionDecisions wrote: the run whose suggestions it decides (null
 * when the header names none), which of its messages (`messageRef`, null when it names none) and each
 * decision. Null when the message isn't one.
 */
export function parseSuggestionDecisions(message: string): { runNumber: number | null; messageRef: string | null; decisions: SuggestionDecision[] } | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const header = HEADER_RE.exec(lines[0]?.trim() ?? '');
  if (!header) return null;
  const decisions: SuggestionDecision[] = [];
  for (let i = 1; i < lines.length; ) {
    const m = LINE_RE.exec(lines[i].trim());
    i++;
    if (!m) continue;
    const more = readFollowingLines(lines, i, NOTE_INDENT);
    i = more.next;
    const note = [(m[4] ?? '').trim(), more.text].filter(Boolean).join('\n');
    decisions.push({ number: Number(m[1]), title: m[2], action: LABEL_ACTIONS.get(m[3])!, note });
  }
  return decisions.length ? { runNumber: header[1] ? Number(header[1]) : null, messageRef: header[2] ?? null, decisions } : null;
}

/** The task ref (SF-T-31, or T-31 from before prefixes) a Backlog or New branch decision made, from its note. */
export function suggestionTaskRef(decision: Pick<SuggestionDecision, 'action' | 'note'>): string | null {
  if (decision.action !== 'backlog' && decision.action !== 'branch') return null;
  return /^(?:[A-Z]{2}-)?T-\d+$/.exec(decision.note.trim())?.[0] ?? null;
}

/**
 * Most factory messages one decline (POST /api/tasks/:taskId/suggestions/decline) names; send more in
 * batches. Small, so one request's messages are saved well inside the suggestions lock's lease.
 */
export const SUGGESTIONS_DECLINE_MAX = 10;

/** A run's number from the factory's label ("Factory · R-12"); null when it names none. */
export function suggestionsRunNumber(label: string | null | undefined): number | null {
  const m = label ? /R-(\d+)$/.exec(label) : null;
  return m ? Number(m[1]) : null;
}

/**
 * The round a decisions message decides, among the rounds before it (oldest first; `runOf` and `idOf`
 * read a round's run and message id): one naming no run, the newest; one naming a message, that one
 * (of its run); one naming only a run, that run's newest.
 */
export function roundForDecisions<R>(
  rounds: R[],
  parsed: { runNumber: number | null; messageRef: string | null },
  runOf: (round: R) => number | null,
  idOf: (round: R) => string
): R | undefined {
  if (parsed.runNumber === null) return rounds[rounds.length - 1];
  const ofRun = rounds.filter((r) => runOf(r) === parsed.runNumber);
  return parsed.messageRef ? ofRun.find((r) => suggestionsMessageRef(idOf(r)) === parsed.messageRef) : ofRun[ofRun.length - 1];
}

/**
 * The message ref a decisions message on `round` names (formatSuggestionDecisions): when its run
 * posted more than one, so it isn't read as the run's newest. Null otherwise. A ref, not a position,
 * so deleting another message doesn't move the decisions.
 */
export function decisionsMessageRef<R>(rounds: R[], round: R, runOf: (round: R) => number | null, idOf: (round: R) => string): string | null {
  const run = runOf(round);
  if (run === null) return null;
  return rounds.filter((r) => runOf(r) === run).length > 1 ? suggestionsMessageRef(idOf(round)) : null;
}

/** One factory message's suggestions on a task, and the numbers people have decided. */
export interface OpenSuggestionRound<M> {
  message: M;
  run: number | null;
  total: number;
  decided: Set<number>;
  /** SF-T-308: Backlog picks handed to the AI builder that it hasn't reported on yet (factory-backlog), by number, with their titles. */
  awaiting: Map<number, string>;
}

/** SF-T-308: a Backlog decision the AI builder makes (no task made by the factory: no ref in its note). */
export const backlogByAgent = (d: Pick<SuggestionDecision, 'action' | 'note'>) => d.action === 'backlog' && !suggestionTaskRef(d);

/**
 * A task's suggestion rounds, oldest first, from its factory messages (`label` "Factory · R-12") and
 * people's decisions messages. A decisions message belongs to the round it names (roundForDecisions),
 * as the task's Suggestions section reads them. Messages without a usable factory-suggestions block
 * are left out.
 */
export function suggestionRoundsOf<M extends { id: string; body: string; label: string | null; created_at: string }>(
  factory: M[],
  decisions: Array<{ body: string; created_at: string }>
): OpenSuggestionRound<M>[] {
  type Entry = { at: string; message?: M; decisions?: string };
  const entries: Entry[] = [...factory.map((m): Entry => ({ at: m.created_at, message: m })), ...decisions.map((d): Entry => ({ at: d.created_at, decisions: d.body }))];
  const rounds: OpenSuggestionRound<M>[] = [];
  for (const entry of entries.sort((a, b) => a.at.localeCompare(b.at))) {
    if (entry.message) {
      // SF-T-308: a report on Backlog picks settles them (before any new round in the same message).
      for (const item of (findFactoryBacklog(entry.message.body) ?? []).filter((i) => i.kind === 'suggestion')) {
        backlogItemRound(rounds, item, (r) => r.run, (r, n) => r.awaiting.get(n) ?? null)?.awaiting.delete(item.number);
      }
      const found = findFactorySuggestions(entry.message.body);
      if (found) rounds.push({ message: entry.message, run: suggestionsRunNumber(entry.message.label), total: found.suggestions.suggestions.length, decided: new Set(), awaiting: new Map() });
      continue;
    }
    const parsed = entry.decisions ? parseSuggestionDecisions(entry.decisions) : null;
    if (!parsed) continue;
    const round = roundForDecisions(rounds, parsed, (r) => r.run, (r) => r.message.id);
    for (const d of parsed.decisions) {
      if (!round || round.decided.has(d.number)) continue;
      round.decided.add(d.number);
      if (backlogByAgent(d)) round.awaiting.set(d.number, d.title);
    }
  }
  return rounds;
}
